/**
 * server-player.ts — stream-rendered audio playback.
 *
 * Plays a continuous sequence of server-rendered Opus chunks via a
 * double-buffered HTML5 Audio strategy. While the current chunk plays,
 * we pre-fetch the next one so playback is seamless.
 *
 * Usage:
 *   const player = new ServerAudioPlayer({ onChunkChange, onError, onPlayingChange })
 *   await player.start({ ragaName, sectionId, baseHz, tempoBpm })
 *   player.pause(); player.resume()
 *   player.stop(); player.dispose()
 *   player.setVolume(0..1)
 */

import { audioUrl, requestRender, type RenderParams, type RenderResult } from './render-client'

// ── timing constants ────────────────────────────────────────────────────────

const FIRST_CHUNK_SEC  = 5      // short first chunk → fast startup latency
const STEADY_CHUNK_SEC = 30     // longer steady-state chunks (better caching)
const CROSSFADE_SEC    = 0.15   // smooth chunk-to-chunk transition
const PREFETCH_LEAD_SEC = 8     // start render of next chunk this many seconds before end

// ── types ──────────────────────────────────────────────────────────────────

export interface ChunkInfo extends RenderResult {
  seq: number
}

export interface PlayerOptions {
  onChunkChange?: (chunk: ChunkInfo) => void
  onError?: (e: Error | unknown) => void
  onPlayingChange?: (playing: boolean) => void
}

export type StartParams = RenderParams & {
  /** Sections array for multi-section concert modes */
  sections?: string[]
  /** Per-section duration overrides */
  sectionDurations?: Record<string, number>
}

// ── player ─────────────────────────────────────────────────────────────────

export class ServerAudioPlayer {
  private onChunkChange: (chunk: ChunkInfo) => void
  private onError: (e: Error | unknown) => void
  private onPlayingChange: (playing: boolean) => void

  private audioA: HTMLAudioElement
  private audioB: HTMLAudioElement
  private activeAudio: HTMLAudioElement
  private queuedAudio: HTMLAudioElement

  private currentParams: (RenderParams & { durationSec: number }) | null = null
  private sections: string[] = ['radio']
  private sectionDurations: Record<string, number> = {}
  private nextChunkPromise: Promise<RenderResult | null> | null = null
  private chunkSeq = 0

  volume = 0.7
  isPlaying = false
  isDisposed = false

  constructor(opts: PlayerOptions = {}) {
    this.onChunkChange    = opts.onChunkChange   ?? (() => {})
    this.onError          = opts.onError         ?? ((e) => console.error('ServerAudioPlayer:', e))
    this.onPlayingChange  = opts.onPlayingChange ?? (() => {})

    this.audioA = new Audio()
    this.audioB = new Audio()
    for (const el of [this.audioA, this.audioB]) {
      el.preload = 'auto'
      el.crossOrigin = 'anonymous'
    }
    this.activeAudio = this.audioA
    this.queuedAudio = this.audioB
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v))
    this.audioA.volume = this.volume
    this.audioB.volume = this.volume
  }

  async start(params: StartParams) {
    if (this.isDisposed) throw new Error('player disposed')
    this.sections = (params.sections?.length ? params.sections : [params.sectionId ?? 'radio'])
    this.sectionDurations = params.sectionDurations ?? {}
    this.currentParams = {
      ragaName:  'Mohanam',
      sectionId: this.sections[0],
      baseHz:    220,
      tempoBpm:  84,
      ...params,
      durationSec: this.sectionDurations[this.sections[0]] ?? (params.durationSec ?? STEADY_CHUNK_SEC),
    }
    this.chunkSeq = 0

    const first = await this._renderChunk(this.chunkSeq++, FIRST_CHUNK_SEC)
    if (this.isDisposed) return

    await this._loadAndPlay(this.activeAudio, first.audioUrl)
    if (!this.isPlaying) return
    this.onChunkChange({ ...first, seq: this.chunkSeq - 1 })
    this._scheduleNextChunk()
  }

  /** Wait for an audio element to be ready to play, then play it. */
  private async _loadAndPlay(el: HTMLAudioElement, src: string): Promise<void> {
    const resolved = audioUrl(src) || src
    const delays = [0, 400, 1200]
    let lastErr: unknown = null
    for (const delayMs of delays) {
      if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs))
      try {
        await this._loadAndPlayOnce(el, resolved)
        return
      } catch (e) {
        lastErr = e
        el.removeAttribute('src')
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('Audio load failed: ' + resolved)
  }

  private _loadAndPlayOnce(el: HTMLAudioElement, src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isDisposed) { resolve(); return }
      const onCanPlay = () => {
        cleanup()
        el.play().then(() => {
          this.isPlaying = true
          this.onPlayingChange(true)
          resolve()
        }).catch((e) => {
          this.onError(e)
          reject(e)
        })
      }
      const onError = () => {
        cleanup()
        reject(new Error('Audio load failed: ' + src))
      }
      const cleanup = () => {
        el.removeEventListener('canplaythrough', onCanPlay)
        el.removeEventListener('error', onError)
      }
      el.addEventListener('canplaythrough', onCanPlay)
      el.addEventListener('error', onError)
      el.src = src
      el.volume = this.volume
      el.load()
    })
  }

  /** Pre-load audio without playing (used for crossfade prep). */
  private _preloadAudio(el: HTMLAudioElement, src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isDisposed) { resolve(); return }
      const onCanPlay = () => { cleanup(); resolve() }
      const onError = () => { cleanup(); reject(new Error('Audio preload failed: ' + src)) }
      const cleanup = () => {
        el.removeEventListener('canplaythrough', onCanPlay)
        el.removeEventListener('error', onError)
      }
      el.addEventListener('canplaythrough', onCanPlay)
      el.addEventListener('error', onError)
      el.src = audioUrl(src) || src
      el.volume = 0
      el.load()
    })
  }

  private _scheduleNextChunk() {
    if (this.isDisposed || !this.activeAudio) return

    // Rendering can be much slower than realtime on a cold service. Begin as
    // soon as the current chunk starts instead of waiting until eight seconds
    // remain; the local synchronized ensemble covers any residual gap.
    if (!this.nextChunkPromise) {
      this.nextChunkPromise = this._renderChunk(this.chunkSeq).catch((e) => {
        this.onError(e)
        return null
      })
    }

    const setupHandlers = () => {
      const onTimeUpdate = async () => {
        if (this.isDisposed || !this.activeAudio.duration) return
        const remaining = this.activeAudio.duration - this.activeAudio.currentTime
        if (remaining < PREFETCH_LEAD_SEC && !this.nextChunkPromise) {
          this.nextChunkPromise = this._renderChunk(this.chunkSeq).catch((e) => {
            this.onError(e)
            return null
          })
        }
      }
      this.activeAudio.addEventListener('timeupdate', onTimeUpdate)

      let crossfadeStarted = false
      const onCrossfadeTick = async () => {
        if (this.isDisposed || !this.activeAudio.duration) return
        const remaining = this.activeAudio.duration - this.activeAudio.currentTime
        if (remaining > CROSSFADE_SEC || crossfadeStarted) return
        crossfadeStarted = true
        await this._beginChunkCrossfade(onTimeUpdate)
      }
      this.activeAudio.addEventListener('timeupdate', onCrossfadeTick)

      const onEnded = async () => {
        if (crossfadeStarted) return
        this.activeAudio.removeEventListener('timeupdate', onCrossfadeTick)
        this.activeAudio.removeEventListener('timeupdate', onTimeUpdate)
        this.activeAudio.removeEventListener('ended', onEnded)
        await this._instantSwap()
      }
      this.activeAudio.addEventListener('ended', onEnded)
    }
    setupHandlers()
  }

  private async _beginChunkCrossfade(onTimeUpdate: EventListener) {
    if (this.isDisposed) return
    let next = this.nextChunkPromise ? await this.nextChunkPromise : null
    if (!next) {
      next = await this._renderChunk(this.chunkSeq).catch((e) => { this.onError(e); return null })
    }
    if (!next || this.isDisposed) return

    const fadeOutEl = this.activeAudio
    const fadeInEl  = this.queuedAudio
    fadeOutEl.removeEventListener('timeupdate', onTimeUpdate)

    // Pre-load next chunk silently (volume = 0) before crossfade
    await this._preloadAudio(fadeInEl, next.audioUrl)
    if (this.isDisposed) return
    try { await fadeInEl.play() } catch (e) { this.onError(e); return }

    const steps = Math.max(8, Math.round(CROSSFADE_SEC * 60))
    const targetVol = this.volume
    for (let i = 1; i <= steps; i++) {
      if (this.isDisposed) return
      const t = i / steps
      fadeOutEl.volume = Math.max(0, targetVol * (1 - t))
      fadeInEl.volume  = Math.min(1, targetVol * t)
      await new Promise<void>(r => setTimeout(r, (CROSSFADE_SEC * 1000) / steps))
    }
    fadeOutEl.pause()
    fadeOutEl.removeAttribute('src')
    fadeInEl.volume = targetVol

    this.activeAudio = fadeInEl
    this.queuedAudio = fadeOutEl
    this.chunkSeq++
    this.onChunkChange({ ...next, seq: this.chunkSeq - 1 })
    this.nextChunkPromise = null
    this._scheduleNextChunk()
  }

  private async _instantSwap() {
    if (this.isDisposed) return
    let next = this.nextChunkPromise ? await this.nextChunkPromise : null
    if (!next) {
      next = await this._renderChunk(this.chunkSeq).catch((e) => { this.onError(e); return null })
    }
    if (!next || this.isDisposed) {
      this.isPlaying = false
      this.onPlayingChange(false)
      return
    }
    const prev = this.activeAudio
    this.activeAudio = this.queuedAudio
    this.queuedAudio = prev
    this.queuedAudio.removeAttribute('src')
    await this._loadAndPlay(this.activeAudio, next.audioUrl).catch(this.onError)
    this.chunkSeq++
    this.onChunkChange({ ...next, seq: this.chunkSeq - 1 })
    this.nextChunkPromise = null
    this._scheduleNextChunk()
  }

  private async _renderChunk(seq: number, durationOverride?: number): Promise<RenderResult & { sectionId: string }> {
    const sectionId = this.sections[seq % this.sections.length]
    const sectionDuration = this.sectionDurations[sectionId] ?? this.currentParams!.durationSec
    const seed = ((this.currentParams!.seed ?? 17) * 31 + seq)
    const params = {
      ...this.currentParams,
      sectionId,
      durationSec: durationOverride ?? sectionDuration,
      seed,
    }
    // Retry transient failures once
    try {
      const result = await requestRender(params)
      return { ...result, sectionId }
    } catch (e) {
      console.warn('[radio] chunk render failed, retrying once:', e)
      await new Promise(r => setTimeout(r, 500))
      const result = await requestRender(params)
      return { ...result, sectionId }
    }
  }

  pause() {
    if (this.activeAudio) this.activeAudio.pause()
    this.isPlaying = false
    this.onPlayingChange(false)
  }

  async resume() {
    if (this.activeAudio?.src) {
      try {
        await this.activeAudio.play()
        this.isPlaying = true
        this.onPlayingChange(true)
      } catch (e) {
        // If play() fails (e.g. user gesture required), try full reload
        this.onError(e)
      }
    }
  }

  stop() {
    if (this.activeAudio) {
      this.activeAudio.pause()
      this.activeAudio.removeAttribute('src')
    }
    if (this.queuedAudio) {
      this.queuedAudio.pause()
      this.queuedAudio.removeAttribute('src')
    }
    this.nextChunkPromise = null
    this.isPlaying = false
    this.onPlayingChange(false)
  }

  async transitionTo(params: StartParams) {
    this.stop()
    await this.start(params)
  }

  dispose() {
    this.isDisposed = true
    this.stop()
    ;(this as Record<string, unknown>).audioA = null
    ;(this as Record<string, unknown>).audioB = null
    ;(this as Record<string, unknown>).activeAudio = null
    ;(this as Record<string, unknown>).queuedAudio = null
  }
}
